import {
  Modal,
  NewButton,
  NewButtonVariant,
} from "@visiblethread/react-components";
import { arrayOf, bool, func, number, shape, string } from "prop-types";

import styles from "./OutlineDocSyncModal.module.scss";

const OutlineDocSyncModal = ({
  isOpen = false,
  isLoading = false,
  affectedDocuments = [],
  onSyncAndSave,
  onSaveOutlineOnly,
  onCancel,
}) => {
  if (!isOpen || affectedDocuments.length === 0) {
    return null;
  }

  return (
    <Modal
      data-testid="outline-doc-sync-modal"
      className="fade-in"
      show
      onDismiss={onCancel}
      backdrop
      centered
    >
      <Modal.Header>Update linked documents?</Modal.Header>

      <Modal.Body>
        <p>The following outline changes affect linked documents:</p>

        {affectedDocuments.map((doc) => (
          <div key={doc.documentId} className={styles.document_card}>
            <p className={styles.document_title}>
              {doc.documentName
                ? `Document: ${doc.documentName}`
                : `Document #${doc.documentId}`}
            </p>

            {doc.renames.length > 0 && (
              <ul className={styles.change_list}>
                {doc.renames.map((rename) => (
                  <li key={rename.outlineItemId} className={styles.change_item}>
                    Rename:{" "}
                    <span className={styles.strikethrough}>
                      {rename.originalTitle}
                    </span>
                    {" → "}
                    <strong>{rename.newTitle}</strong>
                  </li>
                ))}
              </ul>
            )}

            {doc.levelChanges?.length > 0 && (
              <ul className={styles.change_list}>
                {doc.levelChanges.map((change) => (
                  <li key={change.outlineItemId} className={styles.change_item}>
                    Level change: <strong>{change.title}</strong>{" "}
                    <span className={styles.muted}>
                      Level {change.originalLevel} &rarr; Level{" "}
                      {change.newLevel}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {doc.deletions.length > 0 && (
              <ul className={styles.change_list}>
                {doc.deletions.map((deletion) => (
                  <li
                    key={deletion.outlineItemId}
                    className={styles.deletion_item}
                  >
                    Remove:{" "}
                    <span className={styles.strikethrough}>
                      {deletion.title}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {doc.orderChanged && (
              <p className={styles.order_changed}>Sections reordered</p>
            )}
          </div>
        ))}
      </Modal.Body>

      <Modal.Footer>
        <NewButton
          data-testid="cancel-doc-sync"
          variant={NewButtonVariant.l2}
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </NewButton>

        <NewButton
          data-testid="save-outline-only"
          variant={NewButtonVariant.l2}
          onClick={onSaveOutlineOnly}
          disabled={isLoading}
        >
          Save Outline Only
        </NewButton>

        <NewButton
          data-testid="sync-and-save-docs"
          variant={NewButtonVariant.l1}
          onClick={onSyncAndSave}
          isLoading={isLoading}
          disabled={isLoading}
        >
          Save & Update Documents
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

OutlineDocSyncModal.propTypes = {
  isOpen: bool,
  isLoading: bool,
  affectedDocuments: arrayOf(
    shape({
      documentId: number.isRequired,
      documentName: string,
      renames: arrayOf(
        shape({
          outlineItemId: number.isRequired,
          originalTitle: string.isRequired,
          newTitle: string.isRequired,
        })
      ),
      deletions: arrayOf(
        shape({
          outlineItemId: number.isRequired,
          title: string.isRequired,
        })
      ),
      levelChanges: arrayOf(
        shape({
          outlineItemId: number.isRequired,
          title: string.isRequired,
          originalLevel: number.isRequired,
          newLevel: number.isRequired,
        })
      ),
      orderChanged: bool,
    })
  ),
  onSyncAndSave: func,
  onSaveOutlineOnly: func,
  onCancel: func,
};

export default OutlineDocSyncModal;
